import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { X, Upload, FileIcon } from "lucide-react"; // Import icons
import { cn } from "@/lib/shadcn/utils";

interface FileUploadDialoglProps {
    open: boolean; // Changed from show to match shadcn naming
    onClose: () => void; // Changed from handleClose for consistency
    onUpload: (file: File) => void; // Changed from handleFileUpload for consistency
}

const FileUploadDialog: React.FC<FileUploadDialoglProps> = ({ open, onClose, onUpload }) => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setUploadedFile(acceptedFiles[0]);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
    });

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (uploadedFile) {
            onUpload(uploadedFile);
        }
        setUploadedFile(null);
        onClose();
    };

    const handleClose = () => {
        setUploadedFile(null);
        onClose();
    };

    const deleteFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        setUploadedFile(null);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Upload File</DialogTitle>
                    <DialogDescription className="sr-only">
                        Drag and drop a file or click to select one, then upload it.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div
                        {...getRootProps({
                            className: cn(
                                "mt-4 mb-6 border-2 border-dashed rounded-lg p-6",
                                "hover:border-gray-400 transition-colors",
                                "flex flex-col items-center justify-center gap-4",
                                isDragActive && "border-blue-500 bg-blue-50",
                                "cursor-pointer",
                            ),
                        })}
                    >
                        <input {...getInputProps()} />

                        {!uploadedFile ? (
                            <>
                                <Upload className="h-10 w-10 text-gray-400" />
                                <p className="text-sm text-gray-500 text-center">
                                    {isDragActive ? "Drop the file here ..." : "Drag 'n' drop a file here, or click to select"}
                                </p>
                            </>
                        ) : (
                            <div className="w-full">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <FileIcon className="h-8 w-8 text-blue-500" />
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium text-gray-700">{uploadedFile.name}</span>
                                            <span className="text-xs text-gray-500">{(uploadedFile.size / 1024 / 1024).toFixed(1)} MB</span>
                                        </div>
                                    </div>
                                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={deleteFile}>
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!uploadedFile} className="flex items-center gap-2">
                            <Upload className="h-4 w-4" />
                            Upload
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default FileUploadDialog;
